import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useRef} from 'react';
import ReactFlow, {PanOnScrollMode, ReactFlowProps, ReactFlowProvider} from 'reactflow';

import {ActionMethod} from 'constants/AnalyticsConstants';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {REACT_FLOW_PRO_OPTIONS} from 'processes/graph-builder/ReactFlowConstants';
import {GraphBuilderErrorType} from 'processes/graph-builder/validation/GraphBuilderConstants';
import {PROCESS_FLOWCHART_EDGE_TYPE_TO_INTERACTIVE_EDGE_RENDERER} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartInteractiveEdgeRenderer';
import FlowchartCacheDataProvider from 'processes/graph-builder-processes/flowchart-viz/util/FlowchartCacheDataProvider';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHTER} from 'ui-library/Colors';

import ProcessFlowchartEditorControls from './ProcessFlowchartEditorControls';
import {PROCESS_FLOWCHART_NODE_TYPE_TO_INTERACTIVE_NODE_RENDERER} from './ProcessFlowchartInteractiveNodeRenderer';
import {
  DEFAULT_FIT_VIEW_OPTIONS,
  MAX_FLOWCHART_ZOOM,
  MIN_FLOWCHART_ZOOM,
  ProcessFlowchartNodeWidth,
  ProcessInteractiveFlowchart,
} from './ProcessFlowchartVizConstants';
import * as StubbableFunctions from './util/stubbableFunctions';

export interface ProcessFlowchartHorizontalEditorProps {
  flowchart: ProcessInteractiveFlowchart;
  designSpace: ProcessFlowchartDesignSpace;
  locked?: boolean;
  hideSidebar?: boolean;
  onFlowchartChange: (
    flowchart: ProcessInteractiveFlowchart,
    flowchartErrors: Array<GraphBuilderErrorType>
  ) => void;
  reactFlowEmbeddedElement?: JSX.Element | null;
  editorEventGroupId: string;
  actionMethod: ActionMethod;
  didInitialFlowchartHaveChainedRouters: boolean;
  modalType?: string;
}

function getReactFlowInteractionProps(locked: boolean): ReactFlowProps {
  return {
    nodesConnectable: !locked,
    nodesDraggable: !locked,
    zoomOnScroll: false,
    panOnScroll: true,
    panOnScrollMode: PanOnScrollMode.Vertical,
    zoomOnDoubleClick: false,
    panOnDrag: true,
    snapToGrid: true,
  };
}

function EmptyProcessFlowchartHorizontalEditor(props: {locked?: boolean}): JSX.Element {
  return (
    <ReactFlowWrapper width={ProcessFlowchartNodeWidth.WIDE}>
      <ReactFlow
        {...getReactFlowInteractionProps(props.locked ?? false)}
        proOptions={REACT_FLOW_PRO_OPTIONS}
        nodes={[]}
        edges={[]}
        onNodesChange={noop}
        nodeTypes={PROCESS_FLOWCHART_NODE_TYPE_TO_INTERACTIVE_NODE_RENDERER}
        edgeTypes={PROCESS_FLOWCHART_EDGE_TYPE_TO_INTERACTIVE_EDGE_RENDERER}
      />
    </ReactFlowWrapper>
  );
}

/**
 * This is the main component that renders the flowchart.
 *
 * TODO(BNCH-124986): Leverage flowchart editor adapter to render the flowchart.
 * TODO(BNCH-123107): Replace with logic from TransitionModalWithFlowchartEditor.
 */
function InnerProcessFlowchartHorizontalEditor(
  props: ProcessFlowchartHorizontalEditorProps & {reactFlowContainerWidth?: number}
): JSX.Element {
  if (props.reactFlowContainerWidth == null) {
    // We need to wait until the parent's width is known before we render the actual flowchart so ReactFlow
    // can fit the flowchart to the canvas. So here we just render an empty canvas with a hardcoded width.
    return <EmptyProcessFlowchartHorizontalEditor locked={props.locked} />;
  }

  return (
    <ReactFlowWrapper
      width={props.reactFlowContainerWidth}
      containerWidthForControls={props.reactFlowContainerWidth}
    >
      {(reactFlowControlsXPosition) => (
        <div
          className={css(styles.flowchartInnerWrapper)}
          {...getTestProps({component: 'ProcessFlowchartHorizontalEditor', element: 'root'})}
        >
          <div className={css(props.hideSidebar ? styles.flowchartCanvasNoSidebar : styles.flowchartCanvas)}>
            <ReactFlow
              {...getReactFlowInteractionProps(props.locked ?? false)}
              {...getTestProps({component: 'ReactFlow', element: 'root'})}
              proOptions={REACT_FLOW_PRO_OPTIONS}
              nodes={[]}
              edges={[]}
              fitView={true}
              fitViewOptions={DEFAULT_FIT_VIEW_OPTIONS}
              minZoom={MIN_FLOWCHART_ZOOM}
              maxZoom={MAX_FLOWCHART_ZOOM}
            >
              {reactFlowControlsXPosition != null && (
                <ProcessFlowchartEditorControls
                  reactFlowControlsXPosition={reactFlowControlsXPosition}
                  editorEventGroupId={props.editorEventGroupId}
                  actionMethod={props.actionMethod}
                  modalType={props.modalType}
                />
              )}
              {props.reactFlowEmbeddedElement}
            </ReactFlow>
          </div>
        </div>
      )}
    </ReactFlowWrapper>
  );
}

export default function ProcessFlowchartHorizontalEditor(
  props: ProcessFlowchartHorizontalEditorProps
): JSX.Element {
  const reactFlowContainer = useRef<HTMLDivElement>(null);
  const {width: reactFlowContainerWidth} = StubbableFunctions.useResizeObserver({
    ref: reactFlowContainer,
  });
  return (
    <div ref={reactFlowContainer} className={css(styles.flowchartContainer)}>
      <ReactFlowProvider>
        <FlowchartCacheDataProvider>
          <InnerProcessFlowchartHorizontalEditor
            {...props}
            reactFlowContainerWidth={reactFlowContainerWidth}
          />
        </FlowchartCacheDataProvider>
      </ReactFlowProvider>
    </div>
  );
}

const SIDEBAR_WIDTH = 178;

const styles = StyleSheet.create({
  flowchartContainer: {
    width: '100%',
    height: '100%',
    background: GRAY_LIGHTER,
  },
  flowchartInnerWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  flowchartCanvas: {
    width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
  },
  flowchartCanvasNoSidebar: {
    flex: 1,
  },
});
